/**
 * IconSelector 状態管理フック
 */

import { useState, useCallback } from "react";
import { SelectionStep, SelectionState, IconSource } from "../types";

interface UseIconSelectorReturn {
  /** 現在のステップ */
  step: SelectionStep;
  /** 選択状態 */
  selection: SelectionState;
  /** ステップを設定 */
  setStep: (step: SelectionStep) => void;
  /** プリセットアイコンを選択 */
  selectPresetIcon: (url: string) => void;
  /** プロフィールAI生成結果を選択 */
  selectProfileAIResult: (url: string) => void;
  /** 履歴からアセットを選択 */
  selectHistoryAsset: (url: string) => void;
  /** 選択をクリア */
  clearSelection: () => void;
  /** 初期状態にリセット */
  reset: () => void;
  /** 戻る */
  goBack: () => void;
}

const initialSelection: SelectionState = {
  selectedIcon: null,
  source: null,
  selectedFile: null,
};

export const useIconSelector = (): UseIconSelectorReturn => {
  const [step, setStep] = useState<SelectionStep>(SelectionStep.INITIAL);
  const [selection, setSelection] = useState<SelectionState>(initialSelection);

  const selectPresetIcon = useCallback((url: string) => {
    setSelection({
      selectedIcon: url,
      source: IconSource.PRESET,
      selectedFile: null,
    });
  }, []);

  const selectProfileAIResult = useCallback((url: string) => {
    setSelection({
      selectedIcon: url,
      source: IconSource.PROFILE_AI,
      selectedFile: null,
    });
  }, []);

  const selectHistoryAsset = useCallback((url: string) => {
    setSelection({
      selectedIcon: url,
      source: IconSource.HISTORY,
      selectedFile: null,
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelection(initialSelection);
  }, []);

  const reset = useCallback(() => {
    setStep(SelectionStep.INITIAL);
    setSelection(initialSelection);
  }, []);

  const goBack = useCallback(() => {
    if (
      step === SelectionStep.PRESET ||
      step === SelectionStep.PROFILE_AI ||
      step === SelectionStep.MATERIAL
    ) {
      setStep(SelectionStep.INITIAL);
      clearSelection();
    }
  }, [step, clearSelection]);

  return {
    step,
    selection,
    setStep,
    selectPresetIcon,
    selectProfileAIResult,
    selectHistoryAsset,
    clearSelection,
    reset,
    goBack,
  };
};
